/**
 * File navigation.js.
 *
 * Responsive site navigation. One menu, two desktop submenu styles
 * (dropdown by default, full-width mega panel when the top-level item has
 * the CSS class `mega`), accordion submenus inside the mobile panel.
 *
 * All state lives in classes + aria: `body.menu-open` (mobile panel),
 * `li.open` (submenu) and `aria-expanded` on the matching toggle button.
 * The markup contract (submenu-toggle buttons) is produced by
 * ibizz_nav_submenu_toggle() in inc/template-functions.php.
 */
( function () {
	const nav = document.getElementById( 'site-navigation' );
	if ( ! nav ) {
		return;
	}

	// Matches the SCSS `desktop` mixin ($tablet-maxwidth in variables.scss).
	const desktop = window.matchMedia( '(min-width: 1024px)' );

	const menuToggle = nav.querySelector( '.menu-toggle' );
	const parents    = nav.querySelectorAll( 'li.menu-item-has-children' );

	function setOpen( li, open ) {
		li.classList.toggle( 'open', open );
		const btn = li.querySelector( ':scope > .submenu-toggle' );
		if ( btn ) {
			btn.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
		}
	}

	function closeAll( except ) {
		parents.forEach( ( li ) => {
			if ( li !== except && ! ( except && li.contains( except ) ) ) {
				setOpen( li, false );
			}
		} );
	}

	function setPanelOpen( open ) {
		document.body.classList.toggle( 'menu-open', open );
		menuToggle.setAttribute( 'aria-expanded', open ? 'true' : 'false' );
		if ( ! open ) {
			closeAll();
		}
	}

	/* Mobile panel toggle. */
	menuToggle.addEventListener( 'click', () => {
		setPanelOpen( ! document.body.classList.contains( 'menu-open' ) );
	} );

	/* Submenu toggle buttons (click + keyboard, all viewports). */
	nav.addEventListener( 'click', ( e ) => {
		const btn = e.target.closest( '.submenu-toggle' );
		if ( ! btn ) {
			return;
		}
		const li   = btn.closest( 'li' );
		const open = ! li.classList.contains( 'open' );
		if ( open && desktop.matches ) {
			closeAll( li );
		}
		setOpen( li, open );
	} );

	/* Desktop: hover intent — open on real pointer movement, close shortly
	   after leave. Not a plain mouseenter: browsers re-dispatch hover events
	   at the stale cursor position after a page load or layout shift, which
	   opened (then closed) submenus without the mouse ever moving. Only a
	   coordinate change counts as the user actually hovering. */
	let hoverTimer;
	let lastScreenX;
	let lastScreenY;

	function pointerReallyMoved( e ) {
		const moved = lastScreenX !== undefined &&
			( e.screenX !== lastScreenX || e.screenY !== lastScreenY );
		lastScreenX = e.screenX;
		lastScreenY = e.screenY;
		return moved;
	}

	/* Keep the last known position fresh while the cursor is outside the
	   menu items, so the first movement over an item registers as a move. */
	document.addEventListener( 'mousemove', pointerReallyMoved );

	parents.forEach( ( li ) => {
		li.addEventListener( 'mousemove', ( e ) => {
			if ( ! desktop.matches || ! pointerReallyMoved( e ) ) {
				return;
			}
			clearTimeout( hoverTimer );
			if ( ! li.classList.contains( 'open' ) ) {
				closeAll( li );
				setOpen( li, true );
			}
		} );
		li.addEventListener( 'mouseleave', () => {
			if ( ! desktop.matches ) {
				return;
			}
			clearTimeout( hoverTimer );
			hoverTimer = setTimeout( () => setOpen( li, false ), 150 );
		} );

		/* Close when keyboard focus leaves the item entirely. */
		li.addEventListener( 'focusout', ( e ) => {
			if ( desktop.matches && ! li.contains( e.relatedTarget ) ) {
				setOpen( li, false );
			}
		} );
	} );

	/* Escape closes the innermost open thing and restores focus. */
	document.addEventListener( 'keydown', ( e ) => {
		if ( e.key !== 'Escape' ) {
			return;
		}
		const openLi = nav.querySelector( 'li.menu-item-has-children.open' );
		if ( openLi ) {
			closeAll();
			const link = openLi.querySelector( ':scope > a' );
			if ( link && nav.contains( document.activeElement ) ) {
				link.focus();
			}
			return;
		}
		if ( document.body.classList.contains( 'menu-open' ) ) {
			setPanelOpen( false );
			menuToggle.focus();
		}
	} );

	/* Click outside the nav closes open submenus. */
	document.addEventListener( 'click', ( e ) => {
		if ( ! nav.contains( e.target ) ) {
			closeAll();
		}
	} );

	/* Crossing the breakpoint resets all state. */
	desktop.addEventListener( 'change', () => setPanelOpen( false ) );

	/* Back/forward-cache restores the DOM with stale open-state (and resumes
	   pending close-timers) — start every restored view closed. */
	window.addEventListener( 'pageshow', ( e ) => {
		if ( e.persisted ) {
			clearTimeout( hoverTimer );
			setPanelOpen( false );
		}
	} );
}() );

/* Header shadow/state once the page scrolls. */
const header = document.querySelector( '.site-header' );
if ( header ) {
	const scrollThreshold = 50;
	const syncScrolled = () => {
		header.classList.toggle( 'scrolled', window.scrollY >= scrollThreshold );
	};
	window.addEventListener( 'scroll', syncScrolled );
	window.addEventListener( 'DOMContentLoaded', syncScrolled );
}

/* Footer column toggles. */
const footerMiddle = document.querySelector( '.site-footer #footerMiddle' );
if ( footerMiddle ) {
	footerMiddle.querySelectorAll( 'div.toggle' ).forEach( ( toggle ) => {
		toggle.addEventListener( 'click', () => toggle.classList.toggle( 'active' ) );
	} );
}
